import { formatISODate } from "@clipboard-health/date-time";
import { Checkbox, FormControlLabel, FormHelperText, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// eslint-disable-next-line no-restricted-imports -- parseISO treats date-only strings as local time, unlike new Date() which uses UTC
import { parseISO } from "date-fns";
import { type Control, Controller, type FieldErrors, type FieldValues } from "react-hook-form";

import { type JsonSchemaProperty } from "../types";

export interface SchemaFieldProps {
  fieldKey: string;
  property: JsonSchemaProperty;
  fieldPath: string;
  isRequired: boolean;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
}

export function getNestedError(errors: FieldErrors<FieldValues>, path: string): string | undefined {
  const parts = path.split(".");
  let current: unknown = errors;

  for (const part of parts) {
    if (current === undefined || current === null || typeof current !== "object") {
      return undefined;
    }

    current = (current as Record<string, unknown>)[part];
  }

  if (typeof current === "object" && current !== null && "message" in current) {
    return (current as { message?: string }).message;
  }

  return undefined;
}

export function isDateProperty(property: JsonSchemaProperty): boolean {
  return property.type === "string" && property.format === "date";
}

export function isNumberProperty(property: JsonSchemaProperty): boolean {
  return property.type === "number" || property.type === "integer";
}

export function isEnumProperty(property: JsonSchemaProperty): boolean {
  return Array.isArray(property.enum) && property.enum.length > 0;
}

export function isObjectProperty(property: JsonSchemaProperty): boolean {
  return property.type === "object" && property.properties !== undefined;
}

export function isBooleanProperty(property: JsonSchemaProperty): boolean {
  return property.type === "boolean";
}

export function DateField({
  fieldKey,
  fieldPath,
  isRequired,
  control,
  errors,
}: Omit<SchemaFieldProps, "property">) {
  const errorMessage = getNestedError(errors, fieldPath);

  return (
    <Controller
      name={fieldPath}
      control={control}
      rules={isRequired ? { required: "This field is required" } : undefined}
      defaultValue=""
      render={({ field }) => (
        <DatePicker
          label={fieldKey}
          value={field.value ? parseISO(field.value as string) : null}
          format="yyyy-MM-dd"
          slotProps={{
            textField: {
              size: "small",
              error: Boolean(errorMessage),
              helperText: errorMessage,
              sx: { width: 300 },
            },
          }}
          onChange={(date: Date | null) => {
            if (date && !Number.isNaN(date.getTime())) {
              field.onChange(formatISODate(date));
            } else {
              field.onChange("");
            }
          }}
        />
      )}
    />
  );
}

export function NumberField({
  fieldKey,
  fieldPath,
  isRequired,
  control,
  errors,
}: Omit<SchemaFieldProps, "property">) {
  const errorMessage = getNestedError(errors, fieldPath);

  return (
    <Controller
      name={fieldPath}
      control={control}
      rules={{
        ...(isRequired ? { required: "This field is required" } : {}),
        validate: {
          isNumber: (value: unknown) => {
            if (value === "" || value === undefined || value === null) {
              return true;
            }

            return !Number.isNaN(Number(value)) || "Must be a valid number";
          },
        },
      }}
      defaultValue=""
      render={({ field }) => (
        <TextField
          label={fieldKey}
          type="number"
          size="small"
          value={field.value ?? ""}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          sx={{ width: 300 }}
          onChange={(event) => {
            const { value } = event.target;
            field.onChange(value === "" ? "" : Number(value));
          }}
        />
      )}
    />
  );
}

export function StringField({
  fieldKey,
  fieldPath,
  isRequired,
  control,
  errors,
}: Omit<SchemaFieldProps, "property">) {
  const errorMessage = getNestedError(errors, fieldPath);

  return (
    <Controller
      name={fieldPath}
      control={control}
      rules={isRequired ? { required: "This field is required" } : undefined}
      defaultValue=""
      render={({ field }) => (
        <TextField
          label={fieldKey}
          size="small"
          value={field.value ?? ""}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          sx={{ width: 300 }}
          onChange={field.onChange}
        />
      )}
    />
  );
}

export function BooleanField({
  fieldKey,
  fieldPath,
  isRequired,
  control,
  errors,
}: Omit<SchemaFieldProps, "property">) {
  const errorMessage = getNestedError(errors, fieldPath);

  return (
    <Controller
      name={fieldPath}
      control={control}
      rules={
        isRequired
          ? {
              validate: {
                required: (value: unknown) =>
                  typeof value === "boolean" || "This field is required",
              },
            }
          : undefined
      }
      defaultValue={false}
      render={({ field }) => (
        <div>
          <FormControlLabel
            label={fieldKey}
            control={
              <Checkbox
                checked={Boolean(field.value)}
                onChange={(event) => {
                  field.onChange(event.target.checked);
                }}
              />
            }
          />
          {errorMessage ? <FormHelperText error>{errorMessage}</FormHelperText> : null}
        </div>
      )}
    />
  );
}

export function EnumField({
  fieldKey,
  property,
  fieldPath,
  isRequired,
  control,
  errors,
}: SchemaFieldProps) {
  const errorMessage = getNestedError(errors, fieldPath);
  const options = property.enum ?? [];

  return (
    <Controller
      name={fieldPath}
      control={control}
      rules={isRequired ? { required: "This field is required" } : undefined}
      defaultValue=""
      render={({ field }) => (
        <TextField
          select
          label={fieldKey}
          size="small"
          value={field.value ?? ""}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          sx={{ width: 300 }}
          onChange={field.onChange}
        >
          <MenuItem disabled value="">
            Select {fieldKey}
          </MenuItem>
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
}
